import React, { useState, useEffect } from 'react';
import { useAuth } from './context/AuthContext';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from './utils/api';
import PasswordStrengthMeter, { validatePasswordFrontend } from './components/PasswordStrengthMeter';
import './AuthLayout.css';

function LoginPage() {
  const { login, verify2FA, resend2FA, error } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // The sign-in vs. create-account view is driven entirely by the URL so each
  // screen has its own dedicated, shareable address: /login shows sign-in;
  // /register and /signup show the create-account form. Twilio's campaign
  // review crawls /register to confirm the SMS opt-in is visible at the point
  // of phone collection. Toggling between views navigate()s rather than just
  // flipping local state, so the address bar always matches the screen.
  const isRegistering =
    location.pathname === '/register' || location.pathname === '/signup';
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Post-registration verification state
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  // 2FA verification state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [resending2FA, setResending2FA] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  // Mandatory opt-ins (A2P 10DLC compliance). Two separate consents — legal terms
  // and SMS messaging. Both default to false; they must never be pre-selected, or
  // Twilio rejects the campaign.
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToSms, setAgreedToSms] = useState(false);

  // Check URL params for verification status
  useEffect(() => {
    const verified = searchParams.get('verified');
    const verification = searchParams.get('verification');

    if (verified === 'true') {
      setFormSuccess('Email verified successfully! You can now sign in.');
    } else if (verification === 'expired') {
      setFormError('Verification link has expired. Please request a new one.');
    } else if (verification === 'invalid') {
      setFormError('Invalid verification link.');
    } else if (verification === 'error') {
      setFormError('Verification failed. Please try again.');
    }
  }, [searchParams]);

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setLoading(true);

    const result = await login(loginData.email, loginData.password);

    if (!result.success) {
      if (result.requires2FA) {
        setRequires2FA(true);
        setTempToken(result.tempToken);
        setTwoFAError('');
        setVerificationCode('');
        setResendCooldown(60);
        setLoading(false);
        return;
      }
      if (result.code === 'EMAIL_NOT_VERIFIED') {
        setPendingVerification(true);
        setVerificationEmail(loginData.email);
      }
      setFormError(result.error);
    }

    setLoading(false);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (registerData.password !== registerData.confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    const passwordErrors = validatePasswordFrontend(registerData.password);
    if (passwordErrors.length > 0) {
      setFormError(passwordErrors[0]);
      return;
    }

    if (!agreedToTerms) {
      setFormError('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: registerData.email,
          password: registerData.password,
          first_name: registerData.first_name,
          last_name: registerData.last_name,
          phone: registerData.phone,
          role: 'customer'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      setPendingVerification(true);
      setVerificationEmail(registerData.email);

    } catch (err) {
      setFormError(err.message);
    }

    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');

    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });

      const data = await response.json();
      setResendMessage(data.message || 'Verification email sent.');

    } catch (err) {
      setResendMessage('Failed to resend. Please try again.');
    }

    setResendLoading(false);
  };

  const getErrorType = (errorMsg) => {
    if (!errorMsg) return 'error';
    if (errorMsg.includes('temporarily locked') || errorMsg.includes('Too many')) return 'lockout';
    return 'error';
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setTwoFAError('');
    setVerifying(true);

    const result = await verify2FA(verificationCode, tempToken, trustDevice);

    if (!result.success) {
      setTwoFAError(result.error);
    }

    setVerifying(false);
  };

  const handleResend2FA = async () => {
    setResending2FA(true);
    setTwoFAError('');

    const result = await resend2FA(tempToken);

    if (result.success) {
      setResendCooldown(60);
      setVerificationCode('');
    } else {
      setTwoFAError(result.error);
    }

    setResending2FA(false);
  };

  // 2FA verification screen
  if (requires2FA) {
    return (
      <div className="auth-page">
        <div className="auth-bg" style={{ backgroundImage: 'url(/images/login/260318_DGTL_ChariotLoginArt_C4_VeryCompressed.png)' }} />
        <div className="auth-left">
          <div className="auth-container">
            <div className="auth-header">
              <img src="/images/Branding/Horizontal Logo/Chariot_HorizontalLogo_P1_FullColor_011226.png" alt="Chariot" className="auth-logo" />
              <h1>Verification Required</h1>
              <p className="auth-subtitle">Enter the code sent to your email</p>
            </div>

            {twoFAError && (
              <div className="auth-error">{twoFAError}</div>
            )}

            <form className="auth-form" data-autofill="allow" onSubmit={handle2FASubmit}>
              <div className="auth-verification">
                <div className="auth-verification-icon">&#128274;</div>
                <p>We sent a 6-digit verification code to your email for security.</p>
              </div>

              <div className="form-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(val);
                  }}
                  placeholder="000000"
                  required
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  style={{ textAlign: 'center', fontSize: '22px', letterSpacing: '8px', fontFamily: 'monospace' }}
                />
              </div>

              <div className="form-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="trustDevice"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  style={{ width: 'auto', margin: 0 }}
                />
                <label htmlFor="trustDevice" style={{ margin: 0, fontSize: '13px', color: '#64748b', textTransform: 'none', letterSpacing: 0 }}>
                  Trust this device for 7 days
                </label>
              </div>

              <button
                type="submit"
                className="auth-button"
                disabled={verifying || verificationCode.length !== 6}
              >
                {verifying ? 'Verifying...' : 'Verify'}
              </button>

              <div className="auth-footer" style={{ marginTop: '12px' }}>
                <p>
                  Didn't receive a code?{' '}
                  {resendCooldown > 0 ? (
                    <span style={{ color: '#94a3b8' }}>Resend in {resendCooldown}s</span>
                  ) : (
                    <button
                      type="button"
                      className="link-button"
                      onClick={handleResend2FA}
                      disabled={resending2FA}
                    >
                      {resending2FA ? 'Sending...' : 'Resend Code'}
                    </button>
                  )}
                </p>
                <p style={{ marginTop: '8px' }}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setRequires2FA(false);
                      setTempToken('');
                      setVerificationCode('');
                      setTwoFAError('');
                      setTrustDevice(true);
                    }}
                  >
                    Back to Sign In
                  </button>
                </p>
              </div>
            </form>

            <div className="auth-company">
              <p>Poseidon Marine</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pending verification screen
  if (pendingVerification) {
    return (
      <div className="auth-page">
        <div className="auth-bg" style={{ backgroundImage: 'url(/images/login/260318_DGTL_ChariotLoginArt_C4_VeryCompressed.png)' }} />
        <div className="auth-left">
          <div className="auth-container">
            <div className="auth-header">
              <img src="/images/Branding/Horizontal Logo/Chariot_HorizontalLogo_P1_FullColor_011226.png" alt="Chariot" className="auth-logo" />
              <h1>Check Your Email</h1>
              <p className="auth-subtitle">Verify your account to get started</p>
            </div>

            <div className="auth-verification">
              <div className="auth-verification-icon">&#9993;</div>
              <p>We've sent a verification link to:</p>
              <p className="auth-verification-email">{verificationEmail}</p>
              <p className="auth-verification-note">
                Click the link in the email to verify your account. The link expires in 24 hours.
              </p>
            </div>

            {resendMessage && (
              <div className="auth-success">{resendMessage}</div>
            )}

            <button
              type="button"
              className="auth-button auth-button-secondary"
              onClick={handleResendVerification}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending...' : 'Resend Verification Email'}
            </button>

            <div className="auth-footer" style={{ marginTop: '14px' }}>
              <p>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setPendingVerification(false);
                    setFormError('');
                    setVerificationEmail('');
                    setResendMessage('');
                  }}
                >
                  Back to Sign In
                </button>
              </p>
            </div>

            <div className="auth-company">
              <p>Poseidon Marine</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-bg" style={{ backgroundImage: 'url(/images/login/260318_DGTL_ChariotLoginArt_C4_VeryCompressed.png)' }} />
      <div className="auth-left">
        <div className="auth-container">
          {/* Logo / Branding */}
          <div className="auth-header">
            <img src="/images/Branding/Horizontal Logo/Chariot_HorizontalLogo_P1_FullColor_011226.png" alt="Chariot" className="auth-logo" />
            <h1>{isRegistering ? 'Create Account' : 'Sign In'}</h1>
            <p className="auth-subtitle">
              {isRegistering ? 'Register for Poseidon Marine customer portal' : 'Marine Service Management Platform'}
            </p>
          </div>

          {/* Success Display */}
          {formSuccess && (
            <div className="auth-success">{formSuccess}</div>
          )}

          {/* Error Display */}
          {(formError || error) && (
            <div className={`auth-error auth-error-${getErrorType(formError || error)}`}>
              {formError || error}
            </div>
          )}

          {/* Login Form */}
          {!isRegistering && (
            <form className="auth-form" data-autofill="allow" onSubmit={handleLoginSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="forgot-password-row">
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot your password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="auth-button"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <div className="auth-footer">
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setFormError('');
                      setFormSuccess('');
                      navigate('/register');
                    }}
                  >
                    Create one
                  </button>
                </p>
                <p className="auth-disclaimer">
                  By signing in, you agree to our{' '}
                  <a href="/terms">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy">Privacy Policy</a>.
                </p>
              </div>
            </form>
          )}

          {/* Registration Form */}
          {isRegistering && (
            <form className="auth-form" data-autofill="allow" onSubmit={handleRegisterSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={registerData.first_name}
                    onChange={handleRegisterChange}
                    placeholder="John"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={registerData.last_name}
                    onChange={handleRegisterChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reg_email">Email</label>
                <input
                  type="email"
                  id="reg_email"
                  name="email"
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={registerData.phone}
                  onChange={handleRegisterChange}
                  placeholder="(555) 123-4567"
                  required
                  autoComplete="tel"
                />
              </div>

              <div className="form-group">
                <label htmlFor="reg_password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showRegPassword ? 'text' : 'password'}
                    id="reg_password"
                    name="password"
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    placeholder="Min 12 characters"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowRegPassword(!showRegPassword)}
                    aria-label={showRegPassword ? 'Hide password' : 'Show password'}
                  >
                    {showRegPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <PasswordStrengthMeter password={registerData.password} />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={registerData.confirmPassword}
                    onChange={handleRegisterChange}
                    placeholder="Confirm your password"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className="auth-consent">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                />
                <label htmlFor="agreeTerms">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </label>
              </div>

              <div className="auth-consent">
                <input
                  type="checkbox"
                  id="agreeSms"
                  checked={agreedToSms}
                  onChange={(e) => setAgreedToSms(e.target.checked)}
                />
                <label htmlFor="agreeSms">
                  <strong>Optional:</strong> I consent to receive account and service-related text
                  messages from Poseidon Marine at the phone number I provide. Consent is not a
                  condition of any purchase or of using this service. Message frequency varies.
                  Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.
                </label>
              </div>

              <button
                type="submit"
                className="auth-button"
                disabled={
                  loading ||
                  !agreedToTerms ||
                  !registerData.first_name.trim() ||
                  !registerData.last_name.trim() ||
                  !registerData.email.trim() ||
                  !registerData.phone.trim() ||
                  !registerData.password ||
                  !registerData.confirmPassword
                }
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>

              <div className="auth-footer">
                <p>
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setFormError('');
                      setFormSuccess('');
                      navigate('/login');
                    }}
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </form>
          )}

          {/* Company Info */}
          <div className="auth-company">
            <p>Poseidon Marine</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
